'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from 'next-themes';
import {
  Sun,
  Moon,
  Menu,
  X,
  ArrowRight,
  Home,
  User,
  Briefcase,
  Image as ImageIcon,
  BookOpen,
  Mail,
  Calendar,
  Download,
  Sparkles,
} from 'lucide-react';
import { FaGithub, FaLinkedinIn, FaTwitter, FaInstagram } from 'react-icons/fa';

const navLinks = [
  { href: '/', label: 'Home', icon: Home },
  { href: '/about', label: 'About', icon: User },
  { href: '/projects', label: 'Projects', icon: Briefcase },
  { href: '/gallery', label: 'Gallery', icon: ImageIcon },
  { href: '/blog', label: 'Blog', icon: BookOpen },
  { href: '/contact', label: 'Contact', icon: Mail },
];

const socials = [
  { icon: FaGithub, href: 'https://github.com/nasifniloy', label: 'GitHub' },
  { icon: FaLinkedinIn, href: 'https://linkedin.com/in/nasifniloy', label: 'LinkedIn' },
  { icon: FaTwitter, href: 'https://twitter.com/nasifniloy', label: 'Twitter' },
  { icon: FaInstagram, href: 'https://instagram.com/nasifniloy', label: 'Instagram' },
];

export default function Navbar() {
  const pathname = usePathname();
  const { resolvedTheme, setTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileScrolled, setMobileScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 40);
        setMobileScrolled(y > 10);
        ticking = false;
      });
    };
    // Initialize on mount in case the page loads scrolled.
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
  }, [mobileMenuOpen]);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <>
      {/* Desktop navbar */}
      <motion.header
        className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring' as const, stiffness: 120, damping: 20 }}
      >
        <nav className="navbar-inner">
          <Link href="/" className="navbar-logo">
            <span className="navbar-logo-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M4 4H8L16 20H20M4 20L12 4H16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="navbar-logo-text">
              Nasif<span className="navbar-logo-dot">.</span>
            </span>
          </Link>

          <ul className="navbar-links">
            {navLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className={`navbar-link ${isActive(link.href) ? 'navbar-link-active' : ''}`}
                >
                  <span>{link.label}</span>
                  {isActive(link.href) && (
                    <motion.span
                      className="navbar-link-indicator"
                      layoutId="navbar-indicator"
                      transition={{ type: 'spring' as const, stiffness: 350, damping: 30 }}
                    />
                  )}
                </Link>
              </li>
            ))}
          </ul>

          <div className="navbar-actions">
            {mounted && (
              <button
                onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                className="navbar-theme-btn"
                aria-label="Toggle theme"
              >
                <AnimatePresence mode="wait">
                  {resolvedTheme === 'dark' ? (
                    <motion.span key="sun" initial={{ rotate: -90, opacity: 0, scale: 0.5 }} animate={{ rotate: 0, opacity: 1, scale: 1 }} exit={{ rotate: 90, opacity: 0, scale: 0.5 }} transition={{ duration: 0.25 }}>
                      <Sun size={18} />
                    </motion.span>
                  ) : (
                    <motion.span key="moon" initial={{ rotate: 90, opacity: 0, scale: 0.5 }} animate={{ rotate: 0, opacity: 1, scale: 1 }} exit={{ rotate: -90, opacity: 0, scale: 0.5 }} transition={{ duration: 0.25 }}>
                      <Moon size={18} />
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
            )}

            <Link href="/appointment" className="navbar-cta">
              <span>Hire Me</span>
              <ArrowRight size={14} />
            </Link>
          </div>
        </nav>
      </motion.header>

      {/* Mobile top bar */}
      <motion.header
        className={`navbar-mobile ${mobileScrolled ? 'navbar-mobile-scrolled' : 'navbar-mobile-top'}`}
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring' as const, stiffness: 120, damping: 20 }}
      >
        <nav className="navbar-mobile-inner">
          <Link href="/" className="navbar-logo">
            <span className="navbar-logo-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M4 4H8L16 20H20M4 20L12 4H16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="navbar-logo-text">
              Nasif<span className="navbar-logo-dot">.</span>
            </span>
          </Link>

          <div className="navbar-mobile-actions">
            {mounted && (
              <button
                onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                className="navbar-theme-btn"
                aria-label="Toggle theme"
              >
                {resolvedTheme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            )}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="navbar-burger"
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Premium mobile sidebar drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="sidebar-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Sidebar */}
            <motion.aside
              className="sidebar"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring' as const, stiffness: 280, damping: 32 }}
            >
              {/* Decorative gradient orbs */}
              <div className="sidebar-orb sidebar-orb-1" />
              <div className="sidebar-orb sidebar-orb-2" />

              <div className="sidebar-inner">
                {/* Header */}
                <div className="sidebar-header">
                  <div className="sidebar-profile">
                    <div className="sidebar-avatar">
                      <span>N</span>
                      <span className="sidebar-avatar-status" />
                    </div>
                    <div className="sidebar-profile-text">
                      <div className="sidebar-profile-name">Nasif Ahammed</div>
                      <div className="sidebar-profile-role">
                        <Sparkles size={10} />
                        <span>Full-Stack Developer</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="sidebar-close"
                    aria-label="Close menu"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Available status */}
                <motion.div
                  className="sidebar-status-badge"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  <span className="sidebar-status-dot" />
                  <span>Available for new projects</span>
                </motion.div>

                {/* Navigation */}
                <nav className="sidebar-nav">
                  <div className="sidebar-nav-label">Menu</div>
                  <ul>
                    {navLinks.map((link, i) => {
                      const Icon = link.icon;
                      const active = isActive(link.href);
                      return (
                        <motion.li
                          key={link.href}
                          initial={{ opacity: 0, x: 30 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 + i * 0.05, duration: 0.35 }}
                        >
                          <Link
                            href={link.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`sidebar-nav-link ${active ? 'sidebar-nav-link-active' : ''}`}
                          >
                            <span className="sidebar-nav-icon">
                              <Icon size={18} />
                            </span>
                            <span className="sidebar-nav-label-text">{link.label}</span>
                            <span className="sidebar-nav-num">0{i + 1}</span>
                            {active && (
                              <motion.span
                                className="sidebar-nav-active-indicator"
                                layoutId="sidebar-active"
                                transition={{ type: 'spring' as const, stiffness: 300, damping: 30 }}
                              />
                            )}
                          </Link>
                        </motion.li>
                      );
                    })}
                  </ul>
                </nav>

                {/* Action buttons */}
                <motion.div
                  className="sidebar-actions"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Link
                    href="/appointment"
                    onClick={() => setMobileMenuOpen(false)}
                    className="sidebar-cta-primary"
                  >
                    <Calendar size={16} />
                    <span>Book a Meeting</span>
                    <ArrowRight size={14} className="sidebar-cta-arrow" />
                  </Link>

                  <a
                    href="/api/cv/download"
                    className="sidebar-cta-secondary"
                  >
                    <Download size={16} />
                    <span>Download CV</span>
                  </a>
                </motion.div>

                {/* Contact info */}
                <motion.div
                  className="sidebar-contact"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="sidebar-nav-label">Get in Touch</div>
                  <a href="mailto:hello@nasifahammedniloy.com" className="sidebar-contact-item">
                    <Mail size={14} />
                    <span>hello@nasifahammedniloy.com</span>
                  </a>
                </motion.div>

                {/* Footer */}
                <motion.div
                  className="sidebar-footer"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <div className="sidebar-socials">
                    {socials.map((s) => (
                      <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sidebar-social"
                        aria-label={s.label}
                      >
                        <s.icon size={14} />
                      </a>
                    ))}
                  </div>
                  <div className="sidebar-copyright">
                    &copy; 2026 Nasif Ahammed Niloy
                  </div>
                </motion.div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
